import { Injectable } from '@angular/core';
import { CrudService } from '@context/frontend/api-client';
import { Organization } from '@context/shared/types/organization';
import {
	AcceptInviteDto,
	AuthUser,
	Invite,
	User,
} from '@context/shared/types/user';
import {
	collection,
	doc,
	DocumentReference,
	getDoc,
	getDocs,
	query,
	Timestamp,
	where,
} from 'firebase/firestore';
import { firstValueFrom } from 'rxjs';

export type InviteDetailData = {
	authUser: AuthUser | null;
	user: User;
	invite: Invite;
	organization: Organization;
};

export type InviteRequiresSignInData = {
	requiresSignIn: true;
	inviteId: string;
	email: string;
};

function normalizeInviteTimestamps(invite: Invite, docId: string): Invite {
	let expiresAt = invite.expiresAt;
	if (
		expiresAt &&
		typeof expiresAt === 'object' &&
		'_seconds' in expiresAt &&
		'_nanoseconds' in expiresAt
	) {
		const raw = expiresAt as { _seconds: number; _nanoseconds: number };
		expiresAt = new Timestamp(raw._seconds, raw._nanoseconds);
	}
	return { ...invite, expiresAt, id: invite.id || docId };
}

function normalizeInviteDetailResponse(data: {
	invite: Invite;
	user: User;
	organization: Organization;
	authUser: AuthUser | null;
}): InviteDetailData {
	data.invite = normalizeInviteTimestamps(data.invite, data.invite.id ?? '');

	return {
		authUser: data.authUser,
		invite: data.invite,
		user: data.user,
		organization: data.organization,
	};
}

@Injectable({ providedIn: 'root' })
export class InviteService extends CrudService<Invite> {
	static readonly CollectionId = 'invites';

	constructor() {
		super(InviteService.CollectionId, {
			useOrganization: false,
			version: 'v1',
		});
	}

	accept(id: string, data: AcceptInviteDto) {
		return firstValueFrom(
			this.http.post(`${this.api}/v1/users/invite/${id}/accept`, data),
		);
	}

	reject(id: string) {
		return firstValueFrom(
			this.http.get(`${this.api}/v1/users/invite/${id}/reject`),
		);
	}

	authenticate(id: string, email: string) {
		return firstValueFrom(
			this.http.post<{ token: string; orgId: string; expiresAt: Date }>(
				`${this.api}/v1/users/invite/${id}/authenticate`,
				{ email },
			),
		);
	}

	/**
	 * Loads the invite document for an organization user (pending invite flow).
	 * There should be at most one invite per user per organization.
	 */
	async fetchInviteForOrganizationUser(
		organizationId: string,
		userRef: DocumentReference<User>,
	): Promise<Invite | null> {
		const orgRef = doc(
			this.firestore,
			'organizations',
			organizationId,
		) as DocumentReference<Organization>;

		const q = query(
			collection(this.firestore, InviteService.CollectionId),
			where('user', '==', userRef),
			where('organization', '==', orgRef),
		);

		const snapshot = await getDocs(q);

		if (snapshot.docs.length > 1) {
			console.error(
				'InviteService.fetchInviteForOrganizationUser: multiple invites for user in org',
				userRef.id,
				snapshot.docs.map((d) => d.id),
			);
		}

		const docSnap = snapshot.docs[0];
		if (!docSnap) return null;

		return normalizeInviteTimestamps(
			{ ...(docSnap.data() as Invite), id: docSnap.id },
			docSnap.id,
		);
	}

	/**
	 * Loads invite data after authentication
	 */
	async fetchInviteData(
		id: string,
		email: string,
	): Promise<InviteDetailData> {
		// First, fetch the invite to validate it exists
		const invite = await this.findById(id).then(
			(res) => res.data() as Invite,
		);

		if (!invite) throw new Error('Invite not found');

		// Check if invite is expired
		const now = new Date();
		if (invite.expiresAt.toDate().valueOf() < now.valueOf()) {
			throw new Error('Invite has expired');
		}

		// Validate email matches invite
		const decodedEmail = decodeURIComponent(email);
		if (invite.email !== decodedEmail) {
			throw new Error('Email does not match invite');
		}

		// Now fetch the data (should work with temporary auth)
		const user = await getDoc(invite.user as DocumentReference<User>).then(
			(res) => res.data() as User,
		);

		const organization = await getDoc(
			invite.organization as DocumentReference<Organization>,
		).then((res) => res.data() as Organization);

		const authUser = invite.authUser
			? await getDoc(invite.authUser as DocumentReference<AuthUser>).then(
					(res) => res.data() as AuthUser,
				)
			: null;

		return {
			authUser,
			invite,
			user,
			organization,
		};
	}

	/**
	 * Loads invite details via API. Use when authenticated as the invite's user (regular auth).
	 */
	async fetchInviteDetailsFromApi(id: string): Promise<InviteDetailData> {
		return firstValueFrom(
			this.http.get<{
				invite: Invite;
				user: User;
				organization: Organization;
				authUser: AuthUser | null;
			}>(`${this.api}/v1/users/invite/${id}/details`),
		).then((data) => normalizeInviteDetailResponse(data));
	}
}
